/* eslint-disable @typescript-eslint/no-explicit-any */
declare let window: any
import { useCallback } from 'react'
import { useCurrentUserEmail } from 'src/context/authContext'
import { GOOGLE_PICKER_USER_SCOPE } from './GoogleDriveWizard.constants'

/**
 * Google access token for the signed-in browser user, obtained through Google
 * Identity Services (token model). The Google Picker only renders for a user
 * who is signed in to Google in the browser, so the picker must run under the
 * user's own token. Downloads and folder scans keep using the receptor's
 * service-account credentials on the backend.
 */

type CachedToken = {
  accessToken: string
  expiresAt: number
}

// One cache per page load. Google access tokens live about an hour; treat the
// token as expired a minute early so the picker never receives a dying token.
let cachedToken: CachedToken | null = null
const EXPIRY_SKEW_MS = 60_000

export const getCachedGoogleUserToken = (): string | null => {
  if (cachedToken && cachedToken.expiresAt - EXPIRY_SKEW_MS > Date.now()) {
    return cachedToken.accessToken
  }
  return null
}

export const clearCachedGoogleUserToken = (): void => {
  cachedToken = null
}

export class GoogleUserTokenError extends Error {
  readonly code: string

  constructor(code: string, message?: string) {
    super(message || code)
    this.name = 'GoogleUserTokenError'
    this.code = code
  }
}

// Error codes for which the user chose not to proceed; callers stay quiet.
export const GOOGLE_USER_CANCELLED_CODES = new Set([
  'popup_closed',
  'access_denied',
  'interaction_required',
])

/**
 * Requests an access token for the current browser user.
 *
 * Must be called synchronously from a user gesture (a click handler with no
 * `await` before it): GIS opens a popup, and browsers block popups that are
 * not opened inside the gesture's activation window.
 */
export const requestGoogleUserToken = (opts: {
  clientId: string
  hint?: string
}): Promise<string> => {
  const cached = getCachedGoogleUserToken()
  if (cached) {
    return Promise.resolve(cached)
  }

  if (!opts.clientId) {
    return Promise.reject(
      new GoogleUserTokenError(
        'missing_client_id',
        'ntrced has no oauth_gdrive_client_id configured for this environment',
      ),
    )
  }

  const oauth2 = window.google?.accounts?.oauth2
  if (!oauth2) {
    return Promise.reject(
      new GoogleUserTokenError(
        'gsi_not_loaded',
        'Google Identity Services script is not loaded',
      ),
    )
  }

  return new Promise<string>((resolve, reject) => {
    const client = oauth2.initTokenClient({
      client_id: opts.clientId,
      scope: GOOGLE_PICKER_USER_SCOPE,
      hint: opts.hint,
      callback: (resp: any) => {
        if (!resp || resp.error) {
          reject(
            new GoogleUserTokenError(
              resp?.error || 'no_response',
              resp?.error_description,
            ),
          )
          return
        }
        const expiresInSec = Number(resp.expires_in) || 3600
        cachedToken = {
          accessToken: resp.access_token,
          expiresAt: Date.now() + expiresInSec * 1000,
        }
        resolve(resp.access_token)
      },
      error_callback: (err: any) => {
        reject(
          new GoogleUserTokenError(err?.type || 'popup_failed', err?.message),
        )
      },
    })

    // Empty prompt: Google shows the consent screen only the first time this
    // user authorizes the app, and stays silent afterwards while the Google
    // session is alive. Within one page load the cache above avoids even that.
    client.requestAccessToken({ prompt: '' })
  })
}

/**
 * Hook form of requestGoogleUserToken. Takes the OAuth client ID served by
 * ntrced (see useGoogleDrivePickerConfig) and passes the signed-in Trustero
 * user's email as the account hint, so a browser with several Google accounts
 * preselects the matching one.
 */
export const useGoogleUserToken = (
  clientId: string,
): (() => Promise<string>) => {
  const userEmail = useCurrentUserEmail()
  return useCallback(
    () => requestGoogleUserToken({ clientId, hint: userEmail || undefined }),
    [clientId, userEmail],
  )
}
